'use client';

import { Skeleton } from '../../../../components/ui/skeleton';
import { getDeclinedMonthName } from '../../lib/utils';
// ✅ Импортируем React, useMemo, useCallback
import React, { useCallback, useMemo } from 'react';

interface MonthlySummaryData {
  month: number;
  year: number;
  monthName: string;
  averageRate: number | null;
  startOfMonthRate: number | null;
  endOfMonthRate: number | null;
  minRate: number | null;
  maxRate: number | null;
}

interface MonthlyHistoryTableProps {
  monthlySummaries: MonthlySummaryData[];
  loading: boolean;
  charCode: string;
  currencyName: string;
  currentYear: number;
  onMonthClick: (year: number, month: number) => void;
  selectedYear: number;
  selectedMonth?: number;
}

// ✅ Утилита (без изменений, она уже оптимальна)
const formatRate = (rate: number | null | undefined): string => {
  if (typeof rate === 'number' && !isNaN(rate)) {
    return `${rate.toFixed(2)} ₽`;
  }
  return 'N/A';
};

// --- ✅ ОПТИМИЗАЦИЯ 1: Мемоизированные дочерние компоненты ---

/**
 * Мемоизированная кнопка для выбора года.
 * Использует useCallback для клика.
 */
const YearButton = React.memo(
  ({
    year,
    isSelected,
    onClick,
  }: {
    year: number;
    isSelected: boolean;
    onClick: (year: number) => void;
  }) => {
    const handleClick = useCallback(() => {
      onClick(year);
    }, [onClick, year]);

    return (
      <button
        onClick={handleClick}
        className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
          isSelected
            ? 'bg-blue-600 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-blue-700 hover:text-white'
        }`}
        aria-label={`Показать курс за ${year} год`}
      >
        {year}
      </button>
    );
  },
);
YearButton.displayName = 'YearButton';

/**
 * Мемоизированная строка таблицы (Desktop).
 */
const MonthRow = React.memo(
  ({
    monthData,
    isSelected,
    currencyName,
    onClick,
  }: {
    monthData: MonthlySummaryData;
    isSelected: boolean;
    currencyName: string;
    onClick: (year: number, month: number) => void;
  }) => {
    const handleClick = useCallback(() => {
      onClick(monthData.year, monthData.month);
    }, [onClick, monthData.year, monthData.month]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      },
      [handleClick],
    );

    // ✅ ИСПРАВЛЕНИЕ: Убираем пробелы
    return (
      <tr
        role="button"
        tabIndex={0}
        aria-label={`Курс ${currencyName} за ${getDeclinedMonthName(monthData.month, 'nominative')} ${monthData.year}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`transition-colors duration-200 cursor-pointer ${
          isSelected
            ? '[&>td:first-child]:rounded-l-2xl [&>td:last-child]:rounded-r-2xl bg-blue-700 hover:bg-blue-600'
            : 'hover:[&>td]:bg-gray-700'
        }`}
      >
        <td className="px-4 py-2 text-sm font-medium text-white">
          {getDeclinedMonthName(monthData.month, 'nominative')}
        </td>
        <td className="px-4 py-2 text-sm text-gray-300">{formatRate(monthData.averageRate)}</td>
        <td className="px-4 py-2 text-sm text-gray-300">
          {formatRate(monthData.startOfMonthRate)}
        </td>
        <td className="px-4 py-2 text-sm text-gray-300">{formatRate(monthData.endOfMonthRate)}</td>
        <td className="px-4 py-2 text-sm text-gray-300">{formatRate(monthData.minRate)}</td>
        <td className="px-4 py-2 text-sm text-gray-300">{formatRate(monthData.maxRate)}</td>
      </tr>
    );
  },
);
MonthRow.displayName = 'MonthRow';

/**
 * Мемоизированная карточка (Mobile).
 */
const MonthCard = React.memo(
  ({
    monthData,
    isSelected,
    currencyName,
    onClick,
  }: {
    monthData: MonthlySummaryData;
    isSelected: boolean;
    currencyName: string;
    onClick: (year: number, month: number) => void;
  }) => {
    const handleClick = useCallback(() => {
      onClick(monthData.year, monthData.month);
    }, [onClick, monthData.year, monthData.month]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      },
      [handleClick],
    );

    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Курс ${currencyName} за ${getDeclinedMonthName(monthData.month, 'nominative')} ${monthData.year}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`rounded-lg border border-slate-700 p-4 shadow transition-all cursor-pointer ${
          isSelected ? 'bg-blue-700 hover:bg-blue-600' : 'bg-slate-700 hover:bg-slate-600'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        <div className="text-white font-semibold text-base">
          {getDeclinedMonthName(monthData.month, 'nominative')} {monthData.year}
        </div>
        <div className="text-sm text-gray-300 mt-2 space-y-1">
          <div>
            Среднемесячный курс:{' '}
            <span className="font-medium">{formatRate(monthData.averageRate)}</span>
          </div>
          <div>
            Начало месяца:{' '}
            <span className="font-medium">{formatRate(monthData.startOfMonthRate)}</span>
          </div>
          <div>
            Конец месяца:{' '}
            <span className="font-medium">{formatRate(monthData.endOfMonthRate)}</span>
          </div>
          <div>
            Минимум: <span className="font-medium">{formatRate(monthData.minRate)}</span>
          </div>
          <div>
            Максимум: <span className="font-medium">{formatRate(monthData.maxRate)}</span>
          </div>
        </div>
      </div>
    );
  },
);
MonthCard.displayName = 'MonthCard';

// --- Главный компонент ---

export function MonthlyHistoryTable({
  monthlySummaries,
  loading,
  currencyName,
  onMonthClick,
  selectedYear,
  selectedMonth,
}: MonthlyHistoryTableProps) {
  // ✅ ОПТИМИЗАЦИЯ 2: useMemo для вычисляемых данных
  const filteredMonthlySummaries = useMemo(() => {
    return monthlySummaries
      .filter((summary) => summary.year === selectedYear)
      .sort((a, b) => a.month - b.month);
  }, [monthlySummaries, selectedYear]); // ✅ Пересчет только при смене данных или года

  const years = useMemo(() => {
    return Array.from(new Set(monthlySummaries.map((s) => s.year))).sort((a, b) => b - a);
  }, [monthlySummaries]); // ✅ Пересчет только при смене данных

  // ✅ ОПТИМИЗАЦИЯ 3: useCallback для передачи в 'memo' компоненты
  // Эта функция передается в 'YearButton'
  const handleYearClick = useCallback(
    (year: number) => {
      onMonthClick(year, 0); // Сохраняем твою логику (month: 0)
    },
    [onMonthClick],
  );

  // Эта функция передается в 'MonthRow' и 'MonthCard'
  const handleMonthClick = useCallback(
    (year: number, month: number) => {
      onMonthClick(year, month);
    },
    [onMonthClick],
  );

  return (
    <div className="p-6 w-full bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:backdrop-blur-sm dark:border dark:border-slate-700">
      <h3 className="text-xl font-semibold mb-4 text-main">
        Курс {currencyName} в {selectedYear} году по другим месяцам
      </h3>
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 custom-scrollbar">
        {years.map((year) => (
          <YearButton
            key={year}
            year={year}
            isSelected={selectedYear === year}
            onClick={handleYearClick}
          />
        ))}
      </div>
      {loading ? (
        <Skeleton className="w-full h-64" />
      ) : filteredMonthlySummaries.length === 0 ? (
        <div className="text-center text-gray-400 py-10">Нет данных за {selectedYear} год.</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Месяц
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Среднемесячный курс
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Начало месяца
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Конец месяца
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Минимум
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    Максимум
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredMonthlySummaries.map((monthData) => (
                  <MonthRow
                    key={`${monthData.year}-${monthData.month}`}
                    monthData={monthData}
                    isSelected={
                      selectedYear === monthData.year && selectedMonth === monthData.month
                    }
                    currencyName={currencyName}
                    onClick={handleMonthClick}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile Cards */}
          <div className="block sm:hidden space-y-3">
            {filteredMonthlySummaries.map((monthData) => (
              <MonthCard
                key={`${monthData.year}-${monthData.month}`}
                monthData={monthData}
                isSelected={selectedYear === monthData.year && selectedMonth === monthData.month}
                currencyName={currencyName}
                onClick={handleMonthClick}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
